/**
 * structures.ts — shared building + retaining-wall geometry for W2b settlements.
 *
 * Moved out of the client renderer (W2b-2.5) so BOTH the renderer AND server-side
 * collision (Task 2) derive the SAME building boxes and wall lines from a pure
 * function of (CityGraph, PlanetSampler, SettlementIndex). Positions are in
 * PLANET-LOCAL 3D (ready to drop straight into `planet.group`), exactly as the
 * old client builders produced.
 *
 * TWO IMPROVEMENTS over the client's old builders (the visual-pass findings this
 * closes):
 *   - BUILDINGS sit on REAL carved ground: `baseR` is the MINIMUM carved-ground
 *     radius sampled at the 4 footprint corners (with the settlement index
 *     attached to the sampler, so it is the same surface the terrain mesh draws),
 *     sunk {@link BUILDING_SINK_M} so the box foots into the ground rather than
 *     hovering on the frozen parcel level. A footprint whose 4 corner GROUND
 *     heights spread more than {@link BUILDING_MAX_SPREAD_M} is REJECTED (too
 *     steep even after carve — no floating/buried boxes).
 *   - WALLS are CONTINUOUS polyline runs, not per-probe fragments: consecutive
 *     retaining probes with `k ≥ WALL_K_MIN` are merged, and only runs at least
 *     {@link WALL_RUN_MIN_M} long survive (the floating-fragment fix).
 *
 * The salt-0xb1d6 building-height roll is BYTE-IDENTICAL to the old client draw
 * (same `hashInts(planetHash, 0xb1d6, packCell(regionId), parcelIndex)` inputs),
 * so client building HEIGHTS do not change — only the base is now grounded.
 *
 * SHARED-CODE DISCIPLINE: pmath only (hypot/cos/sin/atan2), no `**`; the pmath-ban
 * gate covers this file.
 */
import * as pmath from "../pmath.js";
import { hashSeed } from "../rng.js";
import { hashInts, h01 } from "./hash.js";
import { packCell } from "./cells.js";
import {
  type CityGraph,
  localToDir,
  EDGE_TUNNEL,
  EDGE_BRIDGE,
  USE_MARKET_SQ,
  USE_TEMPLE, USE_PALACE, USE_LOOM_NEXUS,
  USE_GARRISON, USE_WATCHTOWER, USE_PRISON,
  USE_SMITHY, USE_WORKSHOP, USE_PRODUCTION, USE_MILL, USE_WAREHOUSE,
  USE_DEPOT, USE_FUEL_SHED, USE_DOCKYARD,
  USE_MARKET_HALL,
  USE_ACADEMY, USE_APOTHECARY, USE_LOOM_GATE, USE_PRECURSOR,
  USE_HALL, USE_ADMIN, USE_COURTHOUSE, USE_GUILDHALL,
  USE_WELL, USE_MONUMENT, USE_DWELLING,
  USE_BASILICA, USE_EMBASSY, USE_HEADMAN, USE_LANDING_PAD,
} from "./citygraph.js";

/** LANDMARK PARKS (2026-07-31): a hash-picked ~8% of DWELLING parcels carry no
 *  building — the client renders benches + biome trees there and the roster
 *  houses nobody (parcelHousingCapacity 0). ONE rule, three consumers
 *  (structures gate, roster capacity, client landmark layer). */
export function isParkParcel(planetHash: number, rk: number, parcelIndex: number, use: number): boolean {
  if (use !== USE_DWELLING) return false;
  return h01(hashInts(planetHash ^ 0x9a8c, rk, parcelIndex, 7)) < 0.08;
}
import type { PlanetSampler, TerrainSample } from "../universe.js";
import type { SettlementIndex, CarveHit } from "./carveindex.js";
import { obbOf, polygonStrictlyContains } from "./lots.js";
import { districtAt, DISTRICT_H_SCALE } from "./growth.js";
import { NODE_SEED } from "./citygraph.js";
import { BUILDING_ARCHETYPES } from "../buildings/plan.js";

// ── constants (binding) ───────────────────────────────────────────────────────

/**
 * Floats per building instance:
 *   [cx,cy,cz, yaw, w,d,h, tint, baseR, roofPitched, nWindows, chimney, doorYaw,
 *    archetype, buildingSeed]
 *
 * Fields 0..8 are UNCHANGED from Task 4 (grounded box); fields 9..12 are the
 * W2b-2.5 Task-5 building-KIT roll (roof/windows/chimney/door), appended so the
 * kit rolls stay a pure function of the same per-parcel 0xb1d6 stream WITHOUT
 * disturbing the existing draws — see the DRAW-ORDER RULE below and hash.ts.
 *   9  roofPitched  1 = pitched (hip) roof, 0 = flat slab roof
 *   10 nWindows     window quad count, {@link WINDOW_MIN}..{@link WINDOW_MAX}
 *   11 chimney      1 = has a chimney (~{@link CHIMNEY_PROB}), else 0
 *   12 doorYaw      tangent-frame bearing (like `yaw`) toward the NEAREST road
 *                   edge — the road-facing side the door quad is placed on
 *
 * Fields 13..14 are the procedural-buildings extension (stride 13 → 15,
 * drawIndex 4/5 — same append discipline, so fields 0..12 are byte-identical):
 *   13 archetype    integer 0..5 indexing {@link BUILDING_ARCHETYPES}
 *                   (habitation, watchtower, industrial, lab, marketplace,
 *                   outpost) — landmark uses fixed, else a drawIndex-4
 *                   band-weighted roll ({@link archetypeFor})
 *   14 buildingSeed uint32 (exact in a float64) — the drawIndex-5 hash the
 *                   WHOLE building-plan grammar hangs off (plan.ts layerRng
 *                   fans it out per layer under salt 0x51ab)
 *
 * Collision (`settlementCollision.ts`) reads fields 0..5 for the box centre /
 * yaw / footprint, and fields 13..14 (+ the field-6 budget as the plan's hM
 * input) to derive the box HEIGHT via `buildingPlanHeightM` — collision
 * follows the rendered plan's height, not record h (F2).
 *
 * Fields 15..16 are the interiors extension (stride 15 → 17, 2026-07-28 —
 * same append discipline, no new hash draws, fields 0..14 semantics unchanged):
 *   15 use          the parcel's USE_* code (citygraph) — the building TYPE
 *                   (home / store / hall / …) that selects the interior
 *                   template and lets consumers key semantics off the record
 *                   without re-walking the parcel pairing
 *   16 parcelIndex  index into g.parcels — joins the record to the NPC
 *                   roster's homeParcel/workplaceParcel and to parcelDoor
 *
 * Field 17 (stride 17 → 18, 2026-07-29 — P2 fix pass, same discipline):
 *   17 groundSpread max − min of the 4 footprint-corner CARVED ground heights
 *                   (metres, ∈ [0, BUILDING_MAX_SPREAD_M]). The interior floor
 *                   slab is raised by this so the terrain — which sits up to
 *                   spread above the min-corner base — never pokes through an
 *                   interior floor (the "grass carpet indoors" play finding).
 */
export const BUILDING_STRIDE = 18;
/** Building height range (metres), rolled from the per-parcel 0xb1d6 hash.
 *  2026-07-28 interiors scale-up: 5/13 → 6/17; 2026-07-30 "bigger buildings"
 *  user pass: → 7/20 (2–5 storeys at the 4 m module; habitation massing now
 *  climbs to 4). */
export const BUILDING_H_MIN = 7;
export const BUILDING_H_MAX = 20;
/** How far a building foots INTO its grounded base (metres). */
export const BUILDING_SINK_M = 0.4;
/** Building footprint fraction of the lot OBB, per axis (W2d-3).
 *  0.86 → 0.92 (2026-07-30 bigger-buildings pass): buildings FILL their lots
 *  — the cheapest possible footprint growth, zero worldgen cascade (parcels,
 *  streets and counts untouched; the concavity retry ladder still guards). */
export const FOOTPRINT_FRAC = 0.97; // 0.92 → 0.97 (2026-08-02 "bases too small")
/** Footprint extent cap (m) — big civic lots still get house-scaled boxes.
 *  22 → 28 (4 m module) → 34 (bigger-buildings pass) → 38 (2026-08-02):
 *  up to 9 grid cells. */
export const FOOTPRINT_MAX_M = 38;
/** R3 LANDMARKS: the tier flagship uses render OVERSIZED — footprint cap +
 *  height multiplier — a real weenie the settlement organises around.
 *  2026-08-02 (user call "halls, guilds and the like should be massive"): the
 *  map grows from 3 flagship uses to the whole CIVIC class — every hall/guild/
 *  seat-of-power reads at a different scale than the cube houses around it.
 *  Membership also grants the bigger footprint cap + the guaranteed-footprint
 *  concave-lot fallback (buildingFootprint). */
export const LANDMARK_FOOTPRINT_MAX_M = 44;
export const LANDMARK_HEIGHT_MULT: ReadonlyMap<number, number> = new Map([
  [USE_TEMPLE, 1.9], [USE_PALACE, 2.4], [USE_LOOM_NEXUS, 2.7],
  [USE_HALL, 1.6], [USE_HEADMAN, 1.4],
  [USE_MARKET_HALL, 2.0], [USE_GUILDHALL, 1.9],
  [USE_GARRISON, 1.6], [USE_ADMIN, 1.7], [USE_COURTHOUSE, 1.8],
  [USE_ACADEMY, 1.8], [USE_BASILICA, 2.2], [USE_EMBASSY, 1.6],
]);
/** Reject a footprint whose 4 corner ground heights spread beyond this (metres). */
export const BUILDING_MAX_SPREAD_M = 3;

// ── building-kit roll knobs (Task 5; exported so the client layout fn AND its
//    oracle derive the same relations from this single source) ────────────────

/** Roll < this ⇒ a pitched (hip) roof; else a flat slab roof. */
export const ROOF_PITCHED_PROB = 0.7;
/** Roll < this ⇒ the building carries a chimney. */
export const CHIMNEY_PROB = 0.3;
/** Window-quad count range (inclusive) rolled per building. */
export const WINDOW_MIN = 2;
export const WINDOW_MAX = 4;

/**
 * DRAW-ORDER RULE for the salt-0xb1d6 per-parcel stream (why kit rolls do NOT
 * shift building heights). Every 0xb1d6 draw is
 * `hashInts(planetHash, 0xb1d6, packCell(regionId), parcelIndex, drawIndex)`,
 * where `drawIndex` is the FIFTH `hashInts` argument (`e`). The height roll is
 * `drawIndex = 0` — and `hashInts(a,b,c,d)` ≡ `hashInts(a,b,c,d,0,0)`, so the
 * EXISTING height draw is BYTE-IDENTICAL to Task 4. NEW kit rolls take
 * `drawIndex ≥ 1` (1 = roof, 2 = windows, 3 = chimney, and the procedural-
 * buildings extension 4 = archetype roll, 5 = buildingSeed), appended AFTER the
 * height draw and never colliding with it. Adding a kit roll therefore cannot
 * move any height. Each draw is an INDEPENDENT hash keyed by its drawIndex —
 * there is no stream position — so an unused roll shifts nothing (a landmark's
 * drawIndex-4 hash is still computed at the emit site but IGNORED by
 * archetypeFor's fixed-use short-circuit, matching the hash.ts registry note).
 * (structures output is not digested; heights are proven stable by the
 * settlementmesh oracle recomputing the drawIndex-0 formula.)
 */
const DRAW_HEIGHT = 0;
const DRAW_ROOF = 1;
const DRAW_WINDOWS = 2;
const DRAW_CHIMNEY = 3;
/** drawIndex 4 — the archetype band-weighted roll (record field 13). Exported
 *  so the structures oracle recomputes the exact draw. */
export const DRAW_ARCHETYPE = 4;
/** drawIndex 5 — the stored buildingSeed (record field 14, uint32). */
export const DRAW_BSEED = 5;

/** Wall-probe cadence along edges / parcel perimeters (metres). */
export const WALL_PROBE_M = 4;
/** A probe joins a wall run only when the carve is retaining AND k ≥ this. */
export const WALL_K_MIN = 0.35;
/**
 * Drop wall runs shorter than this planet-local length (metres). W2b-2.6: raised
 * 8 → 14 so only substantial retaining runs survive (walls rarer pre-T2).
 */
export const WALL_RUN_MIN_M = 14;

// ── tangent frame (shared with the renderer's reconstruction) ─────────────────

/**
 * A right-handed tangent frame at planet-local point (px,py,pz): `up` is the
 * outward radial, `t1`/`t2` span the tangent plane. Deterministic from the point
 * alone, so a yaw measured against (t1,t2) here is reconstructible by the renderer
 * from the same point (the [x,y,z,yaw,…] orientation contract). Identical to the
 * renderer's `tangentFrame`, but pmath-pure (shared-code discipline).
 */
function tangentFrame(px: number, py: number, pz: number): { up: number[]; t1: number[]; t2: number[] } {
  const l = pmath.hypot(px, py, pz) || 1;
  const up = [px / l, py / l, pz / l];
  const rx = Math.abs(up[1]) < 0.9 ? 0 : 1;
  const ry = Math.abs(up[1]) < 0.9 ? 1 : 0;
  let t1x = ry * up[2] - 0 * up[1];
  let t1y = 0 * up[0] - rx * up[2];
  let t1z = rx * up[1] - ry * up[0];
  const l1 = pmath.hypot(t1x, t1y, t1z) || 1;
  t1x /= l1; t1y /= l1; t1z /= l1;
  const t2x = up[1] * t1z - up[2] * t1y;
  const t2y = up[2] * t1x - up[0] * t1z;
  const t2z = up[0] * t1y - up[1] * t1x;
  return { up, t1: [t1x, t1y, t1z], t2: [t2x, t2y, t2z] };
}

// ── public entry point ────────────────────────────────────────────────────────

/** Rejected generic buildings, in parcel order; authored civics/open spaces are excluded. */
export interface StructureRejection {
  parcelIndex: number;
  use: number;
  reason: "no-contained-footprint" | "invalid-ground" | "ground-spread";
  groundSpreadM?: number;
}

/**
 * Build the settlement's structure geometry for one region CityGraph.
 *
 * Returns PLANET-LOCAL geometry arrays and explicit generic-building rejections:
 *   - `buildings`: [cx,cy,cz, yaw, w,d,h, tint, baseR, roofPitched, nWindows,
 *     chimney, doorYaw, archetype, buildingSeed] per building
 *     ({@link BUILDING_STRIDE} floats). (cx,cy,cz)
 *     is the grounded base-surface CENTRE; yaw the parcel u-axis heading in the
 *     {@link tangentFrame} there; w,d the square footprint (equal); h the 0xb1d6
 *     height; tint the epoch-rank age tint (0 oldest → 1 newest); baseR the base
 *     radius (|(cx,cy,cz)|), i.e. min carved-ground radius at the 4 corners minus
 *     {@link BUILDING_SINK_M}. roofPitched/nWindows/chimney/doorYaw are the Task-5
 *     KIT roll — see BUILDING_STRIDE for the field meanings and the DRAW-ORDER
 *     RULE for why they do not disturb the height draw. The client's pure
 *     `buildingKitLayout` expands these into wall/roof/door/window/chimney parts.
 *     archetype/buildingSeed (fields 13/14, drawIndex 4/5) feed the procedural
 *     building-plan grammar ({@link BUILDING_ARCHETYPES} index + plan seed).
 *   - `wallStrips`: a flat concatenation of ALL RETAINING-WALL RUNS (edge-march
 *     runs first, then parcel-perimeter runs), for the RENDERER (which draws both).
 *     PACKING (exact): each run is `[N, x0,y0,z0,cutH0, …, x{N-1},y{N-1},z{N-1},cutH{N-1}]`
 *     — a leading point-count N (≥ 2) then 4 floats per point (planet-local
 *     position on the carved level surface + cut depth in metres). Runs are laid
 *     end-to-end; a reader takes N, then 4·N floats, and repeats to the end.
 *   - `edgeWallStrips` / `parcelWallStrips`: the SAME runs split by PROVENANCE
 *     (road-edge-march vs parcel-quad-perimeter), same packing. `wallStrips` is
 *     exactly their concatenation (edge ++ parcel). The COLLIDER folds
 *     `parcelWallStrips` ONLY — edge-march runs follow road centrelines and must
 *     not become solid mid-road (recorded decision, W2b-2.5).
 *
 * (R1 D6, 2026-07-10) The perimeter CITY-WALL provenance is REMOVED — no settlement
 * of any tier gets a defensive wall (user finding: "we don't need walls around
 * cities"). Only the terrain-holding RETAINING walls above remain.
 *
 * REQUIRES the `sampler` to carry the settlement `index` (so `sampler.height`
 * returns CARVED ground) — production attaches it via `RegionService`, tests via
 * `setSettlementIndex`. `index` is also used directly to probe wall cut depths.
 */
export function buildStructures(
  g: CityGraph,
  sampler: PlanetSampler,
  index: SettlementIndex,
): { buildings: Float64Array; wallStrips: Float64Array; edgeWallStrips: Float64Array; parcelWallStrips: Float64Array; rejections: StructureRejection[] } {
  const walls = buildWallStrips(g, sampler, index);
  const rejections: StructureRejection[] = [];
  // wallStrips = edge ++ parcel (edge first, preserving the historical byte order).
  const wallStrips = new Float64Array(walls.edge.length + walls.parcel.length);
  wallStrips.set(walls.edge, 0);
  wallStrips.set(walls.parcel, walls.edge.length);
  return {
    buildings: buildBuildings(g, sampler, rejections),
    rejections,
    wallStrips,
    edgeWallStrips: new Float64Array(walls.edge),
    parcelWallStrips: new Float64Array(walls.parcel),
  };
}

// ── buildings ─────────────────────────────────────────────────────────────────

/** An OBB-aligned building footprint on a lot: centre, long-axis dir, extents. */
export interface Footprint { cu: number; cv: number; ax: number; ay: number; w: number; d: number }

/** The 4 chart corners of a footprint, CCW ([x0,y0,…] — ground-sampling points). */
export function footprintCorners(fp: Footprint): Float64Array {
  const hx = fp.ax * fp.w * 0.5, hy = fp.ay * fp.w * 0.5;
  const px = -fp.ay * fp.d * 0.5, py = fp.ax * fp.d * 0.5;
  return new Float64Array([
    fp.cu - hx - px, fp.cv - hy - py,
    fp.cu + hx - px, fp.cv + hy - py,
    fp.cu + hx + px, fp.cv + hy + py,
    fp.cu - hx + px, fp.cv - hy + py,
  ]);
}

/**
 * The building footprint a lot yields (W2d-3): the lot's minimum-area OBB
 * shrunk by {@link FOOTPRINT_FRAC} per axis (capped at {@link FOOTPRINT_MAX_M}),
 * so boxes ALIGN to their lot — and therefore to the street the lot fronts —
 * and FILL it (w ≠ d), replacing the old chart-axis inscribed square that left
 * every parcel mostly empty. Mildly CONCAVE block lots are guarded by an
 * continuous polygon-boundary test with shrink retries; null = no
 * buildable footprint (degenerate sliver, or concave beyond the retries).
 * Exported so the structure oracles derive parcel→building pairing from the
 * SAME rule (grounding/height stay independently verified).
 */
export function buildingFootprint(poly: Float64Array, use = 0): Footprint | null {
  if (poly.length < 6 || poly.length % 2 !== 0 || !poly.every(Number.isFinite)) return null;
  const cap = LANDMARK_HEIGHT_MULT.has(use) ? LANDMARK_FOOTPRINT_MAX_M : FOOTPRINT_MAX_M;
  const obb = obbOf(poly);
  let w = Math.min(obb.long * FOOTPRINT_FRAC, cap);
  let d = Math.min(obb.short * FOOTPRINT_FRAC, cap);
  const fitAt = (cu: number, cv: number): Footprint | null => {
    const fp = { cu, cv, ax: obb.ax, ay: obb.ay, w, d };
    return polygonStrictlyContains(poly, footprintCorners(fp)) ? fp : null;
  };
  for (let attempt = 0; attempt < 7; attempt++) {
    if (Math.min(w, d) < 0.5) break;
    const fp = fitAt(obb.cx, obb.cy);
    if (fp) return fp;
    w *= 0.82;
    d *= 0.82;
  }
  // Landmark recovery: bounded alternative centres inside the OBB, largest size
  // first and a stable tie order. Every candidate obeys the same containment rule.
  if (LANDMARK_HEIGHT_MULT.has(use)) {
    w = Math.min(obb.long * FOOTPRINT_FRAC, cap);
    d = Math.min(obb.short * FOOTPRINT_FRAC, cap);
    for (let attempt = 0; attempt < 12 && Math.min(w, d) >= 0.5; attempt++) {
      for (const x of [0, -0.375, 0.375]) for (const y of [0, -0.375, 0.375]) {
        if (x === 0 && y === 0) continue;
        const fp = fitAt(obb.cx + obb.ax * obb.long * x - obb.ay * obb.short * y,
          obb.cy + obb.ay * obb.long * x + obb.ax * obb.short * y);
        if (fp) return fp;
      }
      w *= 0.82; d *= 0.82;
    }
  }
  return null;
}

/**
 * Bearing (as a {@link tangentFrame} angle, same convention as building `yaw`)
 * from a parcel centroid toward the NEAREST non-TUNNEL road edge — the road-facing
 * side the door quad is placed on. Nearest edge by chart-space point-to-segment
 * distance (ascending edge index breaks ties); the bearing is the 3D tangent
 * direction centroid→closest-point projected onto (t1,t2). Falls back to `yaw`
 * when there is no road edge or the centroid coincides with the closest point.
 */
function doorBearingYaw(
  g: CityGraph, cu: number, cv: number,
  t1: number[], t2: number[], fallbackYaw: number,
): number {
  let bestD2 = Infinity, qu = 0, qv = 0, found = false;
  for (let e = 0; e < g.edgeKind.length; e++) {
    if (g.edgeKind[e] === EDGE_TUNNEL) continue;
    const a = g.edgeA[e], b = g.edgeB[e];
    const au = g.nodeU[a], av = g.nodeV[a], bu = g.nodeU[b], bv = g.nodeV[b];
    const du = bu - au, dv = bv - av;
    const l2 = du * du + dv * dv;
    let t = l2 > 1e-12 ? ((cu - au) * du + (cv - av) * dv) / l2 : 0;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    const ru = au + du * t, rv = av + dv * t;
    const dd = (cu - ru) * (cu - ru) + (cv - rv) * (cv - rv);
    if (dd < bestD2) { bestD2 = dd; qu = ru; qv = rv; found = true; }
  }
  if (!found || bestD2 < 1e-12) return fallbackYaw;
  const dA: number[] = [0, 0, 0], dB: number[] = [0, 0, 0];
  localToDir(g.frame, cu, cv, dA);
  localToDir(g.frame, qu, qv, dB);
  let ex = dB[0] - dA[0], ey = dB[1] - dA[1], ez = dB[2] - dA[2];
  const el = pmath.hypot(ex, ey, ez) || 1; ex /= el; ey /= el; ez /= el;
  return pmath.atan2(
    ex * t2[0] + ey * t2[1] + ez * t2[2],
    ex * t1[0] + ey * t1[1] + ez * t1[2],
  );
}

/**
 * One grounded box per parcel whose poly is at least a quad and whose 4 footprint
 * corners are not too steep (ground spread ≤ {@link BUILDING_MAX_SPREAD_M}).
 * Buildings are emitted in `g.parcels` order over the qualifying parcels.
 */
/** R3 districts: the building height scale at chart (u,v) — core taller, outer
 *  lower (TOWN+ only; band from the nearest seed NODE). Exported so the height-
 *  stability oracle recomputes the exact expected height. F2: delegates to
 *  {@link districtBand} — ONE band derivation, structurally drift-proof
 *  (tier < 3 → band 1, and DISTRICT_H_SCALE[1] === 1, so behavior is
 *  unchanged). */
export function districtHeightScale(g: CityGraph, planetHash: number, cu: number, cv: number): number {
  return DISTRICT_H_SCALE[districtBand(g, planetHash, cu, cv)];
}

/** District BAND (0 core / 1 mid / 2 outer) at chart (u,v) — THE band
 *  derivation ({@link districtHeightScale} delegates here; the archetype
 *  tables consume the band directly). tier < 3 (below TOWN) → mid (1): a
 *  village is one neighbourhood. Exported so the structures oracle recomputes
 *  it. */
export function districtBand(g: CityGraph, planetHash: number, cu: number, cv: number): number {
  if (g.tier < 3) return 1;
  const seeds: { u: number; v: number }[] = [];
  for (let i = 0; i < g.nodeKind.length; i++) {
    if (g.nodeKind[i] === NODE_SEED) seeds.push({ u: g.nodeU[i], v: g.nodeV[i] });
  }
  return districtAt(planetHash, packCell(g.regionId), seeds, g.tier, cu, cv).band;
}

// ── archetype selection (record field 13; procedural-buildings Task 7) ────────

// Archetype indices DERIVED from BUILDING_ARCHETYPES at module init (indexOf),
// so the record values cannot silently desync from that array's order — the
// stored field is BY DEFINITION an index into BUILDING_ARCHETYPES.
const ARCH_HABITATION = BUILDING_ARCHETYPES.indexOf("habitation");
const ARCH_WATCHTOWER = BUILDING_ARCHETYPES.indexOf("watchtower");
const ARCH_INDUSTRIAL = BUILDING_ARCHETYPES.indexOf("industrial");
const ARCH_LAB = BUILDING_ARCHETYPES.indexOf("lab");
const ARCH_MARKETPLACE = BUILDING_ARCHETYPES.indexOf("marketplace");
const ARCH_OUTPOST = BUILDING_ARCHETYPES.indexOf("outpost");

/** Per-band archetype weights (index = archetype, row = band 0 core / 1 mid /
 *  2 outer). Each row sums to 1: core favours civic reads (market, lab), the
 *  outskirts defensive ones (watchtower, outpost); habitation dominates all. */
const ARCHETYPE_WEIGHTS: readonly Float64Array[] = (() => {
  const row = (entries: [number, number][]): Float64Array => {
    const w = new Float64Array(BUILDING_ARCHETYPES.length);
    for (const [i, v] of entries) w[i] = v;
    return w;
  };
  return [
    row([[ARCH_HABITATION, 0.55], [ARCH_INDUSTRIAL, 0.1], [ARCH_LAB, 0.1], [ARCH_MARKETPLACE, 0.2], [ARCH_OUTPOST, 0.05]]),
    row([[ARCH_HABITATION, 0.65], [ARCH_WATCHTOWER, 0.05], [ARCH_INDUSTRIAL, 0.2], [ARCH_MARKETPLACE, 0.1]]),
    row([[ARCH_HABITATION, 0.6], [ARCH_WATCHTOWER, 0.1], [ARCH_INDUSTRIAL, 0.15], [ARCH_OUTPOST, 0.15]]),
  ];
})();

/** USE_* → FIXED archetype (2026-07-28 use-aware selection, the deliberately-
 *  dropped v1 adjacency table's successor): a use with a strong visual identity
 *  always renders it — a garrison reads as a tower, a smithy as a works, a
 *  market hall as a stall mat. Uses absent here keep the band-weighted roll
 *  (dwellings, inns, halls, civic files — habitation-dominant by design).
 *  Landmark short-circuits (temple/loom-nexus/palace) predate this map and
 *  keep their entries for byte-order clarity. */
const USE_ARCHETYPE: ReadonlyMap<number, number> = new Map([
  [USE_TEMPLE, ARCH_LAB], [USE_LOOM_NEXUS, ARCH_LAB], [USE_PALACE, ARCH_OUTPOST],
  [USE_GARRISON, ARCH_WATCHTOWER], [USE_WATCHTOWER, ARCH_WATCHTOWER],
  [USE_PRISON, ARCH_OUTPOST],
  [USE_SMITHY, ARCH_INDUSTRIAL], [USE_WORKSHOP, ARCH_INDUSTRIAL],
  [USE_PRODUCTION, ARCH_INDUSTRIAL], [USE_MILL, ARCH_INDUSTRIAL],
  [USE_WAREHOUSE, ARCH_INDUSTRIAL], [USE_DEPOT, ARCH_INDUSTRIAL],
  [USE_FUEL_SHED, ARCH_INDUSTRIAL], [USE_DOCKYARD, ARCH_INDUSTRIAL],
  [USE_MARKET_HALL, ARCH_MARKETPLACE],
  [USE_ACADEMY, ARCH_LAB], [USE_APOTHECARY, ARCH_LAB],
  [USE_LOOM_GATE, ARCH_LAB], [USE_PRECURSOR, ARCH_LAB],
  // Civic workplaces read DISTINCT from homes (2026-07-31 user call): the
  // bleached lab family for institutions, the armoured outpost for the guild.
  [USE_HALL, ARCH_LAB], [USE_ADMIN, ARCH_LAB], [USE_COURTHOUSE, ARCH_LAB],
  [USE_GUILDHALL, ARCH_OUTPOST],
  // 2026-08-02 civic-landmark wave: the remaining oversized civic uses read as
  // institutions too (they now dwarf the roofline — a band roll would let a
  // basilica render as a house).
  [USE_BASILICA, ARCH_LAB], [USE_EMBASSY, ARCH_LAB], [USE_HEADMAN, ARCH_LAB],
]);

/**
 * The archetype (index into {@link BUILDING_ARCHETYPES}) for a parcel: uses in
 * {@link USE_ARCHETYPE} are FIXED (band and roll ignored — the drawIndex-4
 * hash is still computed at the emit site, matching the hash.ts unused-roll
 * note); every other use walks the band's cumulative weight table with the
 * drawIndex-4 roll ∈ [0,1). Out-of-range band falls back to mid. Exported so
 * the structures oracle recomputes the exact selection.
 */
export function archetypeFor(use: number, band: number, roll: number): number {
  const fixed = USE_ARCHETYPE.get(use);
  if (fixed !== undefined) return fixed;
  const w = ARCHETYPE_WEIGHTS[band] ?? ARCHETYPE_WEIGHTS[1];
  let cum = 0;
  for (let i = 0; i < w.length; i++) {
    cum += w[i];
    if (roll < cum) {
      // NAVIGABILITY (2026-08-02): a DWELLING must never render as the thin
      // watchtower column — its shaft storeys are solid decoration with no way
      // up, which reads as a broken "tower residence" once the observe window
      // labels it a home. Homes fall back to the habitation massing.
      return i === ARCH_WATCHTOWER && use === USE_DWELLING ? ARCH_HABITATION : i;
    }
  }
  return ARCH_HABITATION; // float-residue guard (each row sums to 1)
}

function buildBuildings(g: CityGraph, sampler: PlanetSampler, rejections: StructureRejection[]): Float64Array {
  const frame = g.frame;
  const R = sampler.def.radius;
  const planetHash = hashSeed(sampler.def.seed);
  const rk = packCell(g.regionId);
  const out: number[] = [];
  const d0: number[] = [0, 0, 0];
  const d1: number[] = [0, 0, 0];
  const dc: number[] = [0, 0, 0];

  // Carved ground height (metres) at region-local (u,v): the real surface the
  // terrain mesh draws (sampler carries the index → height is carved).
  const groundH = (u: number, v: number): number => {
    localToDir(frame, u, v, dc);
    return sampler.height(dc[0], dc[1], dc[2]);
  };

  // Epoch rank-percentile among qualifying (quad) parcels — same set the old
  // client builder tinted over, so surviving buildings keep their prior tint.
  // A MARKET_SQ parcel is an OPEN square (spec §Village: "open space, not a
  // building") — reserved by placement but never stamped with a building box.
  // 2026-07-31 LANDMARKS: WELL and MONUMENT parcels are open too (they carry
  // an authored landmark, not a generic building), and a hash-picked slice of
  // dwelling parcels become PARKS (isParkParcel — shared with the roster's
  // housing capacity and the client landmark layer).
  const planetHashL = hashSeed(sampler.def.seed);
  const rkL = packCell(g.regionId);
  const civicAdmins=new Set((g.civics??[]).map(c=>c.admin.parcel));
  const buildable = (par: { poly: Float64Array; use: number }, idx: number): boolean =>
    par.poly.length >= 8 && par.use !== USE_MARKET_SQ
    && !civicAdmins.has(idx)
    && par.use !== USE_WELL && par.use !== USE_MONUMENT
    // 2026-08-02 (user: "landing pads are just generic buildings"): the pad is
    // an OPEN parcel — the client renders an authored platform, not a box.
    && par.use !== USE_LANDING_PAD
    && !isParkParcel(planetHashL, rkL, idx, par.use);

  const epochs: number[] = [];
  for (let p = 0; p < g.parcels.length; p++) {
    if (buildable(g.parcels[p], p)) epochs.push(g.parcels[p].epoch);
  }
  const nQual = epochs.length;

  for (let p = 0; p < g.parcels.length; p++) {
    const parcel = g.parcels[p];
    const poly = parcel.poly;
    if (!buildable(parcel, p)) continue;

    // W2d-3: OBB-aligned footprint filling the lot (w ≠ d), street-aligned by
    // construction. Null = degenerate/too-concave lot — no building.
    const fp = buildingFootprint(poly, parcel.use);
    if (fp === null) {
      rejections.push({ parcelIndex: p, use: parcel.use, reason: "no-contained-footprint" });
      continue;
    }
    const cu = fp.cu, cv = fp.cv;

    // Sample the CARVED ground at the 4 footprint corners. baseR = min corner
    // radius, sunk; reject if the corner ground heights spread too much.
    const fc = footprintCorners(fp);
    const hs: number[] = [
      groundH(fc[0], fc[1]), groundH(fc[2], fc[3]),
      groundH(fc[4], fc[5]), groundH(fc[6], fc[7]),
    ];
    if (!hs.every(Number.isFinite)) {
      rejections.push({ parcelIndex: p, use: parcel.use, reason: "invalid-ground" });
      continue;
    }
    let minH = hs[0], maxH = hs[0];
    for (let k = 1; k < 4; k++) { if (hs[k] < minH) minH = hs[k]; if (hs[k] > maxH) maxH = hs[k]; }
    if (maxH - minH > BUILDING_MAX_SPREAD_M) {
      rejections.push({ parcelIndex: p, use: parcel.use, reason: "ground-spread", groundSpreadM: maxH - minH });
      continue;
    }
    const baseR = R + minH - BUILDING_SINK_M;

    // Base-surface centre (dir at footprint centre × grounded base radius).
    localToDir(frame, cu, cv, d0);
    const px = d0[0] * baseR, py = d0[1] * baseR, pz = d0[2] * baseR;

    // Footprint LONG-axis heading → yaw in the tangent frame (renderer reconstructs).
    localToDir(frame, cu + fp.ax, cv + fp.ay, d1);
    let ex = d1[0] - d0[0], ey = d1[1] - d0[1], ez = d1[2] - d0[2];
    const el = pmath.hypot(ex, ey, ez) || 1;
    ex /= el; ey /= el; ez /= el;
    const f = tangentFrame(px, py, pz);
    const yaw = pmath.atan2(
      ex * f.t2[0] + ey * f.t2[1] + ez * f.t2[2],
      ex * f.t1[0] + ey * f.t1[1] + ez * f.t1[2],
    );

    // Height (4–9 m) — drawIndex 0; BYTE-IDENTICAL to the old client 0xb1d6 draw
    // (hashInts(a,b,c,d) ≡ hashInts(a,b,c,d,0)). See the DRAW-ORDER RULE.
    let h = BUILDING_H_MIN + (BUILDING_H_MAX - BUILDING_H_MIN) * h01(hashInts(planetHash, 0xb1d6, rk, p, DRAW_HEIGHT));
    // R3 landmark: the tier flagship rises well above the roofline. 2026-08-02:
    // the roll is FLOORED first — a civic landmark that rolled the bottom of
    // the height band (7 m × mult ≈ house height) defeated the whole point.
    const lmMult = LANDMARK_HEIGHT_MULT.get(parcel.use);
    if (lmMult !== undefined) h = Math.max(h, BUILDING_H_MAX * 0.85) * lmMult;
    // R3 districts: civic core taller, outskirts lower (shared → collision agrees).
    h *= districtHeightScale(g, planetHash, cu, cv);

    // Kit rolls — drawIndex ≥ 1, appended AFTER the height draw so heights hold.
    const roofPitched = h01(hashInts(planetHash, 0xb1d6, rk, p, DRAW_ROOF)) < ROOF_PITCHED_PROB ? 1 : 0;
    const nWindows = WINDOW_MIN + (hashInts(planetHash, 0xb1d6, rk, p, DRAW_WINDOWS) % (WINDOW_MAX - WINDOW_MIN + 1));
    const chimney = h01(hashInts(planetHash, 0xb1d6, rk, p, DRAW_CHIMNEY)) < CHIMNEY_PROB ? 1 : 0;
    // Door faces the nearest road edge (tangent-frame bearing, like yaw).
    const doorYaw = doorBearingYaw(g, cu, cv, f.t1, f.t2, yaw);

    // Archetype (field 13): landmark uses are fixed (band unused — archetypeFor
    // short-circuits before reading it, so the district lookup is skipped);
    // otherwise a drawIndex-4 weighted roll against the district band's table
    // (core favours civic reads, outer defensive ones).
    const band = lmMult !== undefined ? 1 : districtBand(g, planetHash, cu, cv);
    const archetype = archetypeFor(parcel.use, band,
      h01(hashInts(planetHash, 0xb1d6, rk, p, DRAW_ARCHETYPE)));
    // The whole building-plan grammar hangs off ONE stored seed. drawIndex 5.
    const buildingSeed = hashInts(planetHash, 0xb1d6, rk, p, DRAW_BSEED);

    // Age tint = epoch rank-percentile among qualifying parcels.
    let lt = 0;
    for (let q = 0; q < nQual; q++) if (epochs[q] < parcel.epoch) lt++;
    const tint = nQual > 1 ? lt / (nQual - 1) : 0;

    out.push(px, py, pz, yaw, fp.w, fp.d, h, tint, baseR, roofPitched, nWindows, chimney, doorYaw,
      archetype, buildingSeed, parcel.use, p, maxH - minH);
  }

  return new Float64Array(out);
}

// ── walls ─────────────────────────────────────────────────────────────────────

const _wildScratch: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
const _carveOut: CarveHit = { level: 0, k: 0, kind: 0, dist: 0, half: 0, retaining: false };

/**
 * Continuous retaining-wall runs, split by PROVENANCE. Marches every non-TUNNEL /
 * non-BRIDGE edge (→ `edge`) and every parcel-quad perimeter (→ `parcel`) at
 * {@link WALL_PROBE_M}; consecutive probes that are `retaining && k ≥ WALL_K_MIN`
 * accumulate into a polyline run, flushed when the chain breaks (or the march ends)
 * IF at least {@link WALL_RUN_MIN_M} long. Each array uses the exact packing
 * documented on {@link buildStructures}.
 *
 * WHY SPLIT: edge-march runs follow a ROAD CENTRELINE, so folding them as solid
 * planted walls mid-road (the COLLIDER does this) traps bodies on the road. The
 * collider folds `parcel` (real terraced platform retaining) only; the renderer
 * draws BOTH (visuals unchanged). See settlementCollision.addRegion.
 */
function buildWallStrips(
  g: CityGraph, sampler: PlanetSampler, index: SettlementIndex,
): { edge: number[]; parcel: number[] } {
  const frame = g.frame;
  const R = sampler.def.radius;
  const amp = sampler.amplitude;
  const edge: number[] = [];
  const parcel: number[] = [];
  const d: number[] = [0, 0, 0];

  const wildH = (dx: number, dy: number, dz: number): number =>
    sampler.sample(dx, dy, dz, _wildScratch, false, false, true).h;

  // Probe → { planet-local base position, cut depth } or null (no retaining wall
  // here: absent carve, not retaining, or k below the run threshold).
  const wallProbe = (u: number, v: number): { x: number; y: number; z: number; cut: number } | null => {
    localToDir(frame, u, v, d);
    const w = wildH(d[0], d[1], d[2]);
    if (!index.carveAt(d[0], d[1], d[2], w, _carveOut)) return null;
    if (!_carveOut.retaining || _carveOut.k < WALL_K_MIN) return null;
    const rad = R + _carveOut.level * amp;
    const cut = Math.abs(_carveOut.level - w) * amp;
    return { x: d[0] * rad, y: d[1] * rad, z: d[2] * rad, cut };
  };

  // March one chart segment, accumulating retaining probes into runs on `dst`.
  const marchWall = (dst: number[], u0: number, v0: number, u1: number, v1: number): void => {
    const len = pmath.hypot(u1 - u0, v1 - v0);
    if (len < 1e-6) return;
    const steps = Math.max(1, Math.ceil(len / WALL_PROBE_M));
    let run: number[] = [];       // packed [x,y,z,cut] per point
    let runLen = 0;               // planet-local 3D length so far
    let hx = 0, hy = 0, hz = 0, have = false;
    const flush = (): void => {
      if (run.length >= 8 && runLen >= WALL_RUN_MIN_M) { // ≥ 2 points AND ≥ WALL_RUN_MIN_M
        dst.push(run.length / 4);
        for (let i = 0; i < run.length; i++) dst.push(run[i]);
      }
      run = []; runLen = 0; have = false;
    };
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const p = wallProbe(u0 + (u1 - u0) * t, v0 + (v1 - v0) * t);
      if (p === null) { flush(); continue; }
      if (have) runLen += pmath.hypot(p.x - hx, p.y - hy, p.z - hz);
      run.push(p.x, p.y, p.z, p.cut);
      hx = p.x; hy = p.y; hz = p.z; have = true;
    }
    flush();
  };

  // Walls along road edges (roads on slopes get retained shoulders) — EDGE-march.
  for (let e = 0; e < g.edgeKind.length; e++) {
    const kind = g.edgeKind[e];
    if (kind === EDGE_TUNNEL || kind === EDGE_BRIDGE) continue;
    const a = g.edgeA[e], b = g.edgeB[e];
    marchWall(edge, g.nodeU[a], g.nodeV[a], g.nodeU[b], g.nodeV[b]);
  }
  // Walls around parcel perimeters (terraced platforms cut into grade) — PARCEL-perimeter.
  for (const par of g.parcels) {
    const poly = par.poly;
    if (poly.length < 8) continue;
    const nc = poly.length / 2; // W2d-3: block lots are n-gons
    for (let c = 0; c < nc; c++) {
      const c2 = (c + 1) % nc;
      marchWall(parcel, poly[2 * c], poly[2 * c + 1], poly[2 * c2], poly[2 * c2 + 1]);
    }
  }

  return { edge, parcel };
}
